import type { Tier } from '@/state/GameState';
import type { KritikerId, TerningTall } from './kritikere';

export type KonkurrentBy = {
  konkurrentId: string;
  beloep: number;
};

export type Manuscript = {
  isbn: string;
  title: string;
  author: string;
  genre: string;
  tier: Tier;
  editorial_blurb: string;
  cover_url: string;
  konkurrentBy?: KonkurrentBy;
  fraForfatterId?: string;
};

export type ContractStatus =
  | 'kontrahert'
  | 'redigeres'
  | 'redigert'
  | 'designes'
  | 'designet'
  | 'utgis'
  | 'utgitt';

export type Anmeldelse = {
  kritikerId: KritikerId;
  terninger: TerningTall;
  sitat: string;
  dag: number;
};

export type PlanlagtAnmeldelse = {
  isbn: string;
  kritikerId: KritikerId;
  dag: number;
};

export type CoverEksperimentLagret = {
  lagretDag: number;
  utfall?: 'treff' | 'bom';
};

export type ContractedBook = Manuscript & {
  status: ContractStatus;
  forskudd: number;
  dagKontrahert: number;
  authorId: string;
  faseStartDag: number;
  faseFerdigDag: number | null;
  utgivelsesDag: number | null;
  anmeldelser: Anmeldelse[];
  salg: number;
  inntekt: number;
  dagerUtenSalg: number;
  milestonerNaadd: number[];
  coverEksperiment?: CoverEksperimentLagret;
  ekstraAnmeldelsesBias?: number;
};

export type BookRaw = {
  isbn?: string;
  id?: string;
  title?: string;
  author?: string;
  genre?: string;
  tier?: string;
  editorial_blurb?: string;
  cover_url?: string;
};

export type AuthorStats = {
  dialog: number;
  plot: number;
  karakter: number;
  spraak: number;
  voice: number;
  autentisitet: number;
  disiplin: number;
};

export type PaaKurs = {
  kursId: string;
  startDag: number;
  ferdigDag: number;
};

export type ForfatterArbeider = {
  startDag: number;
  ferdigDag: number;
};

export type KarriereHendelseType =
  | 'kontrahert'
  | 'bok-utgitt'
  | 'bok-bestselger'
  | 'kurs-fullfort'
  | 'pris-nominasjon'
  | 'pris-vinner'
  | 'lojalitet-skifte';

export type KarriereHendelse = {
  dag: number;
  type: KarriereHendelseType;
  beskrivelse: string;
  data?: Record<string, unknown>;
};

export type LojalitetSample = {
  dag: number;
  verdi: number;
};

export const LOJALITET_HISTORIKK_MAX_SAMPLES = 13;
export const KARRIERE_MAX_HENDELSER = 200;

export type Author = {
  id: string;
  navn: string;
  pseudonym: boolean;
  primaerSjanger: string;
  stats: AuthorStats;
  lojalitet: number;
  stress: number;
  ego: number;
  drikkenivaa: number;
  paaKurs?: PaaKurs;
  sistePaaKursDag?: number;
  arbeiderPaa?: ForfatterArbeider;
  sisteLojalitetVarsel?: number;
  sisteInvitasjonDag?: number;
  sisteForfatterEventDag?: number;
  karriere?: KarriereHendelse[];
  lojalitetHistorikk?: LojalitetSample[];
};

export type SvenManusStatus = 'skriver' | 'ferdig' | 'publisert';

export type SvenManusPubliseringsvei =
  | 'eget-forlag'
  | 'kapelle'
  | 'asketank'
  | 'gullsand'
  | 'calidris';

export type SvenManus = {
  startDag: number;
  sjanger: string;
  tittel: string;
  progresjon: number;
  daglig: number;
  status: SvenManusStatus;
  publiseringsvei?: SvenManusPubliseringsvei;
  utgittSomISBN?: string;
};

export type SpillerForfatter = {
  navn: string;
  stats: AuthorStats;
  skrivelabBidrag: number;
  lojalitet: number;
  publiseringsklarErklart: boolean;
  aktivtManus: SvenManus | null;
  manuskriptHistorikk: SvenManus[];
  sisteSkrivDag: number | null;
  brageNominasjoner: number;
  brageVinster: number;
};

export const SPILLER_FORFATTER_PUBLISERINGSKLAR_BIDRAG = 30;
export const SPILLER_FORFATTER_PUBLISERINGSKLAR_SNITT = 60;
export const SVEN_SPILLER_AUTHOR_ID = 'sven-spiller';
export const SPILLER_NAVN_DEFAULT = 'Sven';
export const SPILLER_ETTERNAVN_DEFAULT = 'Henningsen';
export const SPILLER_NAVN_MAX_LENGDE = 30;

export function defaultSpillerForfatter(): SpillerForfatter {
  return {
    navn: SPILLER_NAVN_DEFAULT,
    stats: {
      dialog: 30,
      plot: 30,
      karakter: 30,
      spraak: 30,
      voice: 30,
      autentisitet: 30,
      disiplin: 30,
    },
    skrivelabBidrag: 0,
    lojalitet: 100,
    publiseringsklarErklart: false,
    aktivtManus: null,
    manuskriptHistorikk: [],
    sisteSkrivDag: null,
    brageNominasjoner: 0,
    brageVinster: 0,
  };
}

export function spillerFulltNavn(navn: string): string {
  const trimmed = navn.trim();
  if (trimmed.length === 0) return SPILLER_NAVN_DEFAULT;
  if (trimmed.includes(' ')) return trimmed;
  return `${trimmed} ${SPILLER_ETTERNAVN_DEFAULT}`;
}

export type Course = {
  id: string;
  title: string;
  varighetDager: number;
  pris: number;
  url: string;
  statBoosts: Partial<AuthorStats>;
};

export type CourseRaw = {
  id?: string;
  slug?: string;
  title?: string;
  duration_weeks?: number;
  price_nok?: number;
  url?: string;
  stat_boosts?: Record<string, number>;
};

export type KursFerdigHendelse = {
  authorId: string;
  forfatterNavn: string;
  kursId: string;
  kursTittel: string;
  kursUrl: string;
  boosts: Partial<AuthorStats>;
};

export type EventKategori =
  | 'presse-og-kritikk'
  | 'priser-og-utdelinger'
  | 'bokmesse-og-festival'
  | 'skandaler-og-kontrovers'
  | 'kontrakts-forhandling'
  | 'personlig-drama'
  | 'tidlig-spill';

export type EventTrigger = {
  min_uke?: number;
  maks_uke?: number;
  min_omdomme?: number;
  betingelse?: string;
  dag?: number;
  prioritet?: 'lav' | 'middels' | 'hoy';
};

export type EventKonsekvens = {
  penger?: number;
  omdomme?: number;
  attention?: number;
  salg_boost?: number;
  forfatter_lojalitet?: number;
  forfatter_stress?: number;
  forfatter_tid?: number;
  tid_brukt?: 'halv_dag' | 'hel_dag' | number;
  flag_set?: string;
  risiko?: string;
  forfatter_lojalitet_alle?: number;
  forfatter_disiplin_random?: number;
  manus_i_innboks?: number;
  ny_forfatter?: string;
  [key: string]: number | string | undefined;
};

export type EventValg = {
  tekst: string;
  konsekvenser: EventKonsekvens;
};

export type EventRaw = {
  id: string;
  kategori: EventKategori;
  tittel: string;
  trigger?: EventTrigger;
  beskrivelse: string;
  valg: EventValg[];
};

export type SpillEvent = EventRaw;
